import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { AuthenticationService, ToastService } from '@core/services';
import { from, Observable, throwError } from 'rxjs';

import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';

// RFC7807 Problem Details minimal typing
interface ProblemDetails {
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, unknown>;
  message?: string;
}

function buildBadRequestDetail(problem: ProblemDetails, baseDetail: string): string {
  const errorsRecord = (problem.errors as Record<string, unknown> | undefined) || undefined;
  const rawGeneral = errorsRecord?.['__general'];
  const generalMessages: string[] = Array.isArray(rawGeneral)
    ? rawGeneral.filter((m): m is string => typeof m === 'string' && !!m.trim())
    : [];
  const otherMessages: string[] = errorsRecord
    ? Object.entries(errorsRecord)
        .filter(([k]) => k !== '__general')
        .flatMap(([, v]) => (Array.isArray(v) ? v : []))
        .filter((m): m is string => typeof m === 'string' && !!m.trim())
    : [];
  const allMessages = [...generalMessages, ...otherMessages];

  return allMessages.length
    ? `<div><p>${baseDetail}</p>${allMessages.map(m => `<p>${m}</p>`).join('')}</div>`
    : baseDetail;
}

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthenticationService);
  const toast = inject(ToastService);
  const router = inject(Router);

  const handleError = (error: HttpErrorResponse, problem: ProblemDetails): Observable<never> => {
    const status: number | undefined = error.status || problem.status;
    const title: string = problem.title || problem.message || 'Errore';
    const detail: string = problem.detail || 'Richiesta non completata.';

    if (status === 401) {
      if (error.error?.error === 'password_expired') {
        return throwError(() => error);
      }

      toast.warn('La sessione non è più valida. Effettua nuovamente il login.', 'Sessione Scaduta');

      authService.logout();

      if (router.url.startsWith('/bo')) {
        router.navigate(['/login']);
      } else {
        router.navigate(['/home']);
      }
    } else if (status === 403) {
      toast.warn("Non si dispone dei permessi necessari per effettuare l'operazione.", 'Operazione non consentita');

      router.navigate(['/not-authorized']);
    } else if (status === 400) {
      const detailHtml = buildBadRequestDetail(problem as ProblemDetails, detail);

      toast.warn(detailHtml, title);
    } else if (status === 500) {
      toast.error(detail, title);
    } else {
      const severity = status && status >= 400 && status < 500 ? 'warn' : 'error';

      if (severity === 'warn') {
        toast.warn(detail, title);
      } else {
        toast.error(detail, title);
      }
    }

    return throwError(() => error);
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.error instanceof Blob) {
        return from(error.error.text()).pipe(
          switchMap(text => {
            let problem: ProblemDetails = {};

            try {
              problem = JSON.parse(text);
            } catch {
              problem = {};
            }

            return handleError(error, problem);
          })
        );
      }

      return handleError(error, error.error ?? {});
    })
  );
};
